import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
    PiChartLineUp,
    PiPlus,
    PiTrendDown,
    PiTrendUp,
} from 'react-icons/pi';
import SensitiveMoney from '../components/SensitiveMoney';

import AccountPageHeader from '../components/AccountPageHeader';
import PageIntro from '../components/PageIntro';
import PeriodNavigator from '../components/PeriodNavigator';
import PeriodContentTransition from '../components/PeriodContentTransition';
import TransactionsSection from '../components/TransactionsSection';
import AddItemModal from '../components/AddItemModal';
import EditItemModal from '../components/EditItemModal';
import RecurringEditScopeSheet from '../components/RecurringEditScopeSheet';
import RecurringDeleteScopeSheet from '../components/RecurringDeleteScopeSheet';
import ConfirmDeleteModal from '../components/ConfirmDeleteModal';
import ShareTransactionSheet from '../components/ShareTransactionSheet';
import ChangeAttributionSheet from '../components/ChangeAttributionSheet';
import FiltersModal from '../components/FilterModal';
import MemberAttributionFilter from '../components/MemberAttributionFilter';

import { useFinancialFeedback } from '../hooks/useFinancialFeedback';
import { haptics } from '../utils/haptics';
import { useTransactions } from '../hooks/useTransactions';
import { useFinancialOccurrences } from '../hooks/useFinancialOccurrences';
import { remoteOperationMessage } from '../services/connectivityService';

import {
    FIRESTORE_TRANSACTION_STATUS,
    TRANSACTION_STATUS,
    TRANSACTION_TYPES,
    createOccurrenceKey,
    fromFirestoreTransaction,
    formatShortCivilDate,
    getAmountCents,
    getFinancialDate,
    getTodayCivilDate,
    getUpcomingFinancialRange,
    isCivilDateInRange,
    isRealizedTransaction,
} from '../domain/finance';

import { compareYearMonthKeys, formatYearMonthLabel, fromYearMonthParam, getCurrentYearMonthKey, toYearMonthParam } from '../utils/period';
import { normalizeString } from '../utils';
import { getMovementTemporalContext, withMovementTemporalContext, formatMovementRangeLabel } from './movementsTemporalContext';
import { buildDateRangeSeries, buildHistorySeries, splitMovementOccurrences } from './movementsFutureContext';
import {
    createRecurringTransactionFromDraft,
    getRecurringOccurrenceStateByTransactionId,
    getRecurringSegment,
    splitRecurringSeriesFromOccurrence,
    materializeRecurringOccurrence,
    updateMaterializedRecurringOccurrence,
    skipRecurringOccurrence,
    cancelRecurringSeriesFromOccurrence,
} from '../services/recurringTransactionService';

import './Movements.css';
import FinancialFlowChart from '../components/FinancialFlowChart';
import { getVisibleFlowSeries } from '../components/FinancialFlowChart/seriesVisibility';
import { useAccountMembers } from '../hooks/useAccountMembers';
import { filterTransactionsByMember, MEMBER_FILTER_ALL } from '../domain/finance/memberAttribution';

const CATEGORY_COLORS = [
    'var(--nf-blue)',
    'var(--nf-income)',
    'var(--nf-expense)',
    'var(--nf-pending)',
    'var(--nf-slate)',
];

export const getNavigationDateRange = (view, today = getTodayCivilDate()) => {
    if (view === 'future') {
        return getUpcomingFinancialRange(today);
    }

    if (view === 'recent') {
        return { startDate: '', endDate: today };
    }

    return { startDate: '', endDate: '' };
};

export const filterTransactionsByDateRange = (items, startDate, endDate) => items.filter(
    (item) => isCivilDateInRange(getFinancialDate(item), startDate || null, endDate || null)
);

const CATEGORY_ANALYSIS_CONTEXTS = {
    all: { title: 'Gastos por categoria', empty: 'Nenhuma despesa neste período.', semantic: 'expense' },
    expense: { title: 'Gastos por categoria', empty: 'Nenhuma despesa neste período.', semantic: 'expense' },
    income: { title: 'Receitas por categoria', empty: 'Nenhuma receita neste período.', semantic: 'income' },
    pending: { title: 'Compromissos por categoria', empty: 'Nenhuma movimentação pendente neste período.', semantic: 'neutral' },
};

const getCategoryBreakdown = (items) => {
    const groups = new Map();

    items.forEach((item) => {
        const amount = getAmountCents(item);

        if (amount === null) return;

        const category =
            item.categoria || 'Sem categoria';

        groups.set(
            category,
            (groups.get(category) || 0) + amount
        );
    });

    const total = Array.from(
        groups.values()
    ).reduce(
        (sum, value) => sum + value,
        0
    );

    return Array.from(groups.entries())
        .map(([name, value]) => ({
            name,
            value,
            percentage:
                total > 0
                    ? (value / total) * 100
                    : 0,
        }))
        .sort((a, b) => b.value - a.value)
        .slice(0, 5);
};

const buildMonthlySeries = (
    receitas,
    despesas,
    selectedPeriod
) => {
    const [rawMonth, rawYear] =
        String(selectedPeriod)
            .split('/')
            .map(Number);

    if (!rawMonth || !rawYear) {
        return [];
    }

    const daysInMonth =
        new Date(
            rawYear,
            rawMonth,
            0
        ).getDate();

    const bucketCount = 6;
    const bucketSize =
        Math.ceil(
            daysInMonth / bucketCount
        );

    return Array.from(
        { length: bucketCount },
        (_, index) => {
            const from =
                index * bucketSize + 1;

            const to = Math.min(
                from + bucketSize - 1,
                daysInMonth
            );

            const sumBucket = (items) =>
                items
                    .filter((item) => {
                        const date =
                            getFinancialDate(item);

                        if (!date) return false;

                        const day =
                            Number(
                                date.split('-')[2]
                            );

                        return (
                            day >= from &&
                            day <= to
                        );
                    })
                    .reduce(
                        (sum, item) =>
                            sum +
                            (getAmountCents(item) || 0),
                        0
                    );

            const incomeItems = receitas.filter((item) => {
                const date = getFinancialDate(item);
                if (!date) return false;
                const day = Number(date.split('-')[2]);
                return day >= from && day <= to;
            });

            const expenseItems = despesas.filter((item) => {
                const date = getFinancialDate(item);
                if (!date) return false;
                const day = Number(date.split('-')[2]);
                return day >= from && day <= to;
            });

            const prefix = `${rawYear}-${String(rawMonth).padStart(2, '0')}-`;
            const startDate = prefix + String(from).padStart(2, '0');
            const endDate = prefix + String(to).padStart(2, '0');
            return {
                startDate,
                endDate,
                rangeLabel: formatShortCivilDate(startDate, true) + ' — ' + formatShortCivilDate(endDate, true),
                label:
                    from === to
                        ? String(from)
                        : `${from}-${to}`,
                income:
                    sumBucket(receitas),
                expense:
                    sumBucket(despesas),
                count:
                    incomeItems.length + expenseItems.length,
            };
        }
    );
};

const Movements = ({ currentAccountId, currentRole, currentUser, accounts }) => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();


    const {
        showSuccess,
        showError,
    } = useFinancialFeedback();

    const currentPeriod =
        getCurrentYearMonthKey();
    const navigationView = searchParams.get('view');
    const isFutureView = navigationView === 'future';
    const navigationDateRange = useMemo(
        () => getNavigationDateRange(navigationView),
        [navigationView]
    );

    const selectedPeriod = fromYearMonthParam(searchParams.get('period')) || currentPeriod;
    const [periodDirection, setPeriodDirection] = useState('forward');
    const temporal = useMemo(
        () => getMovementTemporalContext(searchParams, selectedPeriod, navigationDateRange),
        [searchParams, selectedPeriod, navigationDateRange]
    );
    const isAllView = temporal.mode === 'all';
    const isRecentView = temporal.mode === 'recent';
    const isPersistedHistoryView = isAllView || isRecentView;
    const activeOccurrenceRange = temporal.range;
    const history = useTransactions(
        currentAccountId,
        isPersistedHistoryView ? null : selectedPeriod,
        isAllView
            ? { enabled: true, allPeriods: true, complete: true }
            : isRecentView
                ? { enabled: true, endDate: navigationDateRange.endDate, complete: true }
                : { enabled: false }
    );
    const { addTransaction, updateTransaction, deleteTransaction, toggleTransactionStatus } = history;
    const {
        occurrences,
        loading: occurrenceLoading,
        error: occurrenceError,
        resolvedRange,
        resolvedAccountId,
        retry: retryOccurrences,
    } = useFinancialOccurrences({
        accountId: currentAccountId,
        startDate: activeOccurrenceRange?.startDate,
        endDate: activeOccurrenceRange?.endDate,
        enabled: Boolean(activeOccurrenceRange),
    });
    const hasResolvedActiveRange = Boolean(activeOccurrenceRange)
        && resolvedAccountId === currentAccountId
        && resolvedRange?.startDate === activeOccurrenceRange.startDate
        && resolvedRange?.endDate === activeOccurrenceRange.endDate;
    const hasResolvedDataset = isPersistedHistoryView
        ? history.isResolved && history.resolvedAccountId === currentAccountId
        : hasResolvedActiveRange;
    const loading = isPersistedHistoryView ? history.loading : occurrenceLoading;
    const transactionsError = temporal.error || (isPersistedHistoryView ? history.error : occurrenceError);
    const resolvedTransactions = useMemo(
        () => splitMovementOccurrences(hasResolvedDataset ? (isPersistedHistoryView ? history.transactions : occurrences) : []),
        [hasResolvedDataset, isPersistedHistoryView, history.transactions, occurrences]
    );
    const receitas = resolvedTransactions.receitas;
    const despesas = resolvedTransactions.despesas;
    const currentAccount = accounts.find((account) => account.id === currentAccountId) || null;
    const { members } = useAccountMembers(currentAccount ? currentAccountId : null);
    const [memberFilter, setMemberFilter] = useState(MEMBER_FILTER_ALL);
    const resolvedItems = useMemo(() => [...receitas, ...despesas], [receitas, despesas]);
    useEffect(() => { setMemberFilter(MEMBER_FILTER_ALL); }, [currentAccountId]);
    const retryTransactions = isPersistedHistoryView ? history.retry : retryOccurrences;

    const [
        searchQuery,
        setSearchQuery,
    ] = useState('');
    const [quickFilter, setQuickFilter] = useState(() => searchParams.get('status') === 'pending' ? 'pending' : 'all');

    const [
        showAddModal,
        setShowAddModal,
    ] = useState(false);

    const [
        addModalInitialType,
        setAddModalInitialType,
    ] = useState(
        TRANSACTION_TYPES.INCOME
    );

    const [
        editItem,
        setEditItem,
    ] = useState(null);
    const [editOccurrenceState, setEditOccurrenceState] = useState(null);
    const [recurringEditTarget, setRecurringEditTarget] = useState(null);
    const [editLookupLoading, setEditLookupLoading] = useState(false);
    const [scopeActionLoading, setScopeActionLoading] = useState(false);
    const editRequestInFlight = useRef(false);
    const scopeActionInFlight = useRef(false);

    const handleRequestEdit = useCallback(async (item) => {
        if (!item || editRequestInFlight.current) return;

        if (item.isVirtual === true) {
            setRecurringEditTarget({ item, occurrenceState: null });
            return;
        }

        editRequestInFlight.current = true;
        setEditLookupLoading(true);
        try {
            const occurrenceState = await getRecurringOccurrenceStateByTransactionId({
                accountId: currentAccountId,
                transactionId: item.id,
            });
            if (occurrenceState) {
                setRecurringEditTarget({ item, occurrenceState });
            } else {
                setEditOccurrenceState(null);
                setEditItem(item);
            }
        } catch (error) {
            console.error('Erro ao identificar recorrência:', error);
            showError('Não foi possível verificar esta movimentação. Tente novamente.');
        } finally {
            editRequestInFlight.current = false;
            setEditLookupLoading(false);
        }
    }, [currentAccountId, showError]);

    const editTransactionId = searchParams.get('edit');

    useEffect(() => {
        if (!editTransactionId || loading) return;

        const target = [...receitas, ...despesas]
            .find((item) => String(item.id) === editTransactionId);

        if (target) handleRequestEdit(target);

        setSearchParams((currentParams) => {
            const nextParams = new URLSearchParams(currentParams);
            nextParams.delete('edit');
            return nextParams;
        }, { replace: true });
    }, [despesas, editTransactionId, handleRequestEdit, loading, receitas, setSearchParams]);

    const [
        deleteItem,
        setDeleteItem,
    ] = useState(null);

    const [recurringDeleteTarget, setRecurringDeleteTarget] = useState(null);
    const [deleteLookupLoading, setDeleteLookupLoading] = useState(false);
    const [deleteActionLoading, setDeleteActionLoading] = useState(false);
    const deleteRequestInFlight = useRef(false);
    const deleteActionInFlight = useRef(false);

    const handleRequestDelete = useCallback(async (item) => {
        if (!item || deleteRequestInFlight.current) return;
        deleteRequestInFlight.current = true;
        setDeleteLookupLoading(true);
        try {
            if (item.isVirtual === true) {
                const segment = await getRecurringSegment({
                    accountId: currentAccountId,
                    segmentId: item.segmentId,
                });
                setRecurringDeleteTarget({ item, occurrenceState: null, segment });
                void haptics.warning();
                return;
            }
            const occurrenceState = await getRecurringOccurrenceStateByTransactionId({
                accountId: currentAccountId,
                transactionId: item.id,
            });
            if (!occurrenceState) {
                setDeleteItem(item);
                void haptics.warning();
                return;
            }
            const segment = await getRecurringSegment({
                accountId: currentAccountId,
                segmentId: occurrenceState.segmentId,
            });
            setRecurringDeleteTarget({ item, occurrenceState, segment });
            void haptics.warning();
        } catch (error) {
            console.error('Erro ao identificar recorrência para exclusão:', error);
            showError('Não foi possível verificar esta movimentação. Tente novamente.');
        } finally {
            deleteRequestInFlight.current = false;
            setDeleteLookupLoading(false);
        }
    }, [currentAccountId, showError]);
    const [
        shareItem,
        setShareItem,
    ] = useState(null);
    const [attributionItem, setAttributionItem] = useState(null);

    const [
        showFilterModal,
        setShowFilterModal,
    ] = useState(false);

    const filterStartDate = temporal.mode === 'range' ? searchParams.get('start') || '' : '';
    const filterEndDate = temporal.mode === 'range' ? searchParams.get('end') || '' : '';
    const allPeriods = isAllView;
    const [filterStatus, setFilterStatus] = useState('');
    const [filterCategory, setFilterCategory] = useState('');
    const periodReceitas = useMemo(() => filterTransactionsByMember(receitas, memberFilter), [receitas, memberFilter]);
    const periodDespesas = useMemo(() => filterTransactionsByMember(despesas, memberFilter), [despesas, memberFilter]);
    const applyTemporalPeriod = ({ mode, startDate, endDate }) => {
        setSearchParams((params) => {
            const next = withMovementTemporalContext(params, { mode, startDate, endDate });
            next.set('period', toYearMonthParam(selectedPeriod));
            return next;
        });
    };

    const filtersActive =
        Boolean(
            filterStartDate ||
            filterEndDate ||
            filterStatus ||
            filterCategory ||
            allPeriods ||
            memberFilter !== MEMBER_FILTER_ALL ||
            quickFilter !== 'all'
        );

    const applyFilters = useCallback((
        items,
        type
    ) => {
        let result = items;

        if (
            (quickFilter === 'income' && type !== TRANSACTION_TYPES.INCOME) ||
            (quickFilter === 'expense' && type !== TRANSACTION_TYPES.EXPENSE)
        ) {
            return [];
        }

        if (quickFilter === 'pending') {
            result = result.filter(
                (item) => isRealizedTransaction({ ...item, tipo: type }) === false
            );
        }
        if (filterCategory) {
            result = result.filter(
                (item) =>
                    item.categoria ===
                    filterCategory
            );
        }

        if (filterStatus) {
            if (
                filterStatus ===
                TRANSACTION_STATUS.PAID
            ) {
                result = result.filter(
                    (item) =>
                        isRealizedTransaction({
                            ...item,
                            tipo: type,
                        }) === true
                );
            } else if (
                filterStatus ===
                TRANSACTION_STATUS.PENDING
            ) {
                result = result.filter(
                    (item) =>
                        isRealizedTransaction({
                            ...item,
                            tipo: type,
                        }) === false
                );
            }
        }

        if (searchQuery) {
            const query =
                normalizeString(
                    searchQuery
                ).toLowerCase();

            result = result.filter(
                (item) =>
                    normalizeString(
                        item.nome || ''
                    )
                        .toLowerCase()
                        .includes(query)
            );
        }

        return result;
    }, [
        filterCategory,
        filterStatus,
        quickFilter,
        searchQuery,
    ]);

    const visibleReceitas =
        useMemo(
            () =>
                applyFilters(
                    periodReceitas,
                    TRANSACTION_TYPES.INCOME
                ),
            [applyFilters, periodReceitas]
        );

    const visibleDespesas =
        useMemo(
            () =>
                applyFilters(
                    periodDespesas,
                    TRANSACTION_TYPES.EXPENSE
                ),
            [applyFilters, periodDespesas]
        );

    const visibleChartSeries = useMemo(() => getVisibleFlowSeries(quickFilter, {
        income: visibleReceitas.length > 0,
        expense: visibleDespesas.length > 0,
    }), [quickFilter, visibleReceitas.length, visibleDespesas.length]);

    const chartData =
        useMemo(
            () => isPersistedHistoryView
                ? buildHistorySeries(visibleReceitas, visibleDespesas)
                : temporal.mode !== 'month'
                ? buildDateRangeSeries(
                    visibleReceitas,
                    visibleDespesas,
                    activeOccurrenceRange?.startDate,
                    activeOccurrenceRange?.endDate
                )
                : buildMonthlySeries(
                    visibleReceitas,
                    visibleDespesas,
                    selectedPeriod
                ),
            [
                isPersistedHistoryView,
                temporal.mode,
                activeOccurrenceRange,
                visibleReceitas,
                visibleDespesas,
                selectedPeriod,
            ]
        );

    const categoryContext = CATEGORY_ANALYSIS_CONTEXTS[quickFilter] || CATEGORY_ANALYSIS_CONTEXTS.all;
    const categoryTransactions = useMemo(() => {
        if (quickFilter === 'income') return visibleReceitas;
        if (quickFilter === 'pending') return [...visibleReceitas, ...visibleDespesas];
        return visibleDespesas;
    }, [quickFilter, visibleReceitas, visibleDespesas]);
    const categories = useMemo(() => getCategoryBreakdown(categoryTransactions), [categoryTransactions]);
    const categoryTotal = categoryTransactions.reduce((sum, item) => sum + (getAmountCents(item) || 0), 0);

    const categoryGradient =
        useMemo(() => {
            if (categories.length === 0) {
                return 'conic-gradient(var(--nf-surface-3) 0 100%)';
            }

            let current = 0;

            const stops =
                categories.map(
                    (category, index) => {
                        const start = current;
                        const end =
                            current +
                            category.percentage;

                        current = end;

                        return `${CATEGORY_COLORS[
                            index %
                            CATEGORY_COLORS.length
                            ]
                            } ${start}% ${end}%`;
                    }
                );

            return `conic-gradient(${stops.join(', ')})`;
        }, [categories]);

    const transactionsCount =
        visibleReceitas.length +
        visibleDespesas.length;

    const incomeTotal =
        visibleReceitas.reduce(
            (sum, item) =>
                sum +
                (getAmountCents(item) || 0),
            0
        );

    const expenseTotal =
        visibleDespesas.reduce(
            (sum, item) =>
                sum +
                (getAmountCents(item) || 0),
            0
        );

    const balance =
        incomeTotal -
        expenseTotal;

    const openAddModal = (type) => {
        const preferredType =
            type ||
            localStorage.getItem(
                'lastUsedType'
            );

        setAddModalInitialType(
            preferredType ===
                TRANSACTION_TYPES.EXPENSE
                ? TRANSACTION_TYPES.EXPENSE
                : TRANSACTION_TYPES.INCOME
        );

        setShowAddModal(true);
    };

    const resetFilters = () => {
        setFilterStatus('');
        setFilterCategory('');
        setSearchQuery('');
        setQuickFilter('all');
        setMemberFilter(MEMBER_FILTER_ALL);
        if (!isFutureView) applyTemporalPeriod({ mode: 'month' });
    };

    const handleAdd = async (item) => {
        try {
            await addTransaction(item);

            showSuccess(
                item.tipo ===
                    TRANSACTION_TYPES.INCOME
                    ? 'A receita foi adicionada com sucesso.'
                    : 'A despesa foi adicionada com sucesso.',
                'Movimentação adicionada'
            );
            retryTransactions();
        } catch (error) {
            console.error(
                'Erro ao adicionar movimentação:',
                error
            );

            showError(
                remoteOperationMessage(error, 'Não foi possível adicionar a movimentação.')
            );

            throw error;
        }
    };

    const handleAddRecurring = async ({ transaction, recurrence }) => {
        try {
            await createRecurringTransactionFromDraft({
                accountId: currentAccountId,
                actorUid: currentUser.uid,
                transaction,
                recurrence,
            });
            retryTransactions();
            showSuccess(
                'Recorrência criada com sucesso.',
                'Movimentação recorrente criada'
            );
        } catch (error) {
            console.error('Erro ao criar recorrência:', error);
            showError(remoteOperationMessage(error, 'Não foi possível criar a recorrência. Tente novamente.'));
            throw error;
        }
    };

    const handleEditOnlyThis = async () => {
        if (!recurringEditTarget || scopeActionInFlight.current) return;

        const { item, occurrenceState } = recurringEditTarget;
        if (occurrenceState) {
            setEditOccurrenceState(occurrenceState);
            setEditItem(item);
            setRecurringEditTarget(null);
            return;
        }

        scopeActionInFlight.current = true;
        setScopeActionLoading(true);
        try {
            const scheduledDate = getFinancialDate(item);
            if (
                !item.occurrenceKey
                || !item.seriesId
                || !item.segmentId
                || createOccurrenceKey(item.seriesId, scheduledDate) !== item.occurrenceKey
            ) {
                throw new Error('INVALID_RECURRING_OCCURRENCE');
            }
            const materialized = await materializeRecurringOccurrence({
                accountId: currentAccountId,
                actorUid: currentUser.uid,
                segmentId: item.segmentId,
                scheduledDate,
                occurrenceKey: item.occurrenceKey,
                status: FIRESTORE_TRANSACTION_STATUS.PENDING,
            });
            const transaction = fromFirestoreTransaction(
                materialized.transaction.id,
                materialized.transaction
            );
            setEditOccurrenceState(materialized.occurrenceState);
            setEditItem(transaction);
            setRecurringEditTarget(null);
            retryTransactions();
        } catch (error) {
            console.error('Erro ao materializar ocorrência para edição:', error);
            showError(remoteOperationMessage(error, 'Não foi possível preparar esta ocorrência para edição. Tente novamente.'));
        } finally {
            scopeActionInFlight.current = false;
            setScopeActionLoading(false);
        }
    };

    const handleEditThisAndFollowing = async () => {
        if (!recurringEditTarget || scopeActionInFlight.current) return;
        scopeActionInFlight.current = true;
        setScopeActionLoading(true);
        try {
            const { item, occurrenceState } = recurringEditTarget;
            const scheduledDate = occurrenceState?.scheduledDate || getFinancialDate(item);
            const segmentId = occurrenceState?.segmentId || item.segmentId;
            const seriesId = occurrenceState?.seriesId || item.seriesId;
            if (!segmentId || !seriesId || !scheduledDate
                || createOccurrenceKey(seriesId, scheduledDate) !== (occurrenceState?.occurrenceKey || item.occurrenceKey)) {
                throw new Error('INVALID_RECURRING_OCCURRENCE');
            }
            const segment = await getRecurringSegment({ accountId: currentAccountId, segmentId });
            if (segment.seriesId !== seriesId) throw new Error('RECURRING_SERIES_MISMATCH');
            setEditOccurrenceState({
                scope: 'following', occurrenceState, segment, scheduledDate,
            });
            setEditItem(item);
            setRecurringEditTarget(null);
        } catch (error) {
            console.error('Erro ao preparar edição futura:', error);
            showError('Não foi possível preparar esta recorrência. Tente novamente.');
        } finally {
            scopeActionInFlight.current = false;
            setScopeActionLoading(false);
        }
    };

    const handleEdit = async (item) => {
        try {
            if (editOccurrenceState?.scope === 'following') {
                await splitRecurringSeriesFromOccurrence({
                    accountId: currentAccountId,
                    actorUid: currentUser.uid,
                    segmentId: editOccurrenceState.segment.id,
                    expectedRevision: editOccurrenceState.segment.revision,
                    scheduledDate: editOccurrenceState.scheduledDate,
                    anchorDate: item.transaction.date,
                    transaction: item.transaction,
                    recurrence: item.recurrence,
                });
            } else if (editOccurrenceState) {
                await updateMaterializedRecurringOccurrence({
                    accountId: currentAccountId,
                    actorUid: currentUser.uid,
                    occurrenceKey: editOccurrenceState.occurrenceKey,
                    transaction: item,
                });
            } else {
                await updateTransaction(item);
            }

            showSuccess(
                'As alterações foram salvas.',
                'Movimentação atualizada'
            );
            retryTransactions();
        } catch (error) {
            console.error(
                'Erro ao editar movimentação:',
                error
            );

            const splitConflict = editOccurrenceState?.scope === 'following'
                && error?.code === 'RECURRENCE_SPLIT_CONFLICT';
            showError(
                remoteOperationMessage(error, splitConflict
                    ? 'Esta recorrência foi alterada em outro lugar. Reabra e tente novamente.'
                    : editOccurrenceState
                        ? 'Não foi possível salvar esta ocorrência. Nenhuma alteração parcial foi aplicada.'
                        : 'Não foi possível salvar as alterações.')
            );

            throw error;
        }
    };

    const handleChangeAttribution = async (item, attributedToUid, attributedToNameSnapshot) => {
        try {
            await updateTransaction({
                ...item,
                attributedToUid: attributedToUid || null,
                attributedToNameSnapshot: attributedToUid ? attributedToNameSnapshot : null,
            });
            showSuccess('A atribuição foi atualizada.', 'Movimentação atualizada');
            retryTransactions();
        } catch (error) {
            console.error('Erro ao alterar atribuição:', error);
            showError(remoteOperationMessage(error, 'Não foi possível alterar a atribuição. Tente novamente.'));
            throw error;
        }
    };

    const handleDelete =
        async (id, tipo) => {
            try {
                await deleteTransaction(
                    id,
                    tipo
                );

                showSuccess(
                    'A movimentação foi excluída.',
                    'Movimentação excluída'
                );
                retryTransactions();
            } catch (error) {
                console.error(
                    'Erro ao excluir movimentação:',
                    error
                );

                showError(
                    remoteOperationMessage(error, 'Não foi possível excluir a movimentação.')
                );

                throw error;
            }
        };

    const handleDeleteRecurringOnlyThis = async ({ allowRealized = false } = {}) => {
        if (!recurringDeleteTarget || deleteActionInFlight.current) return;
        deleteActionInFlight.current = true;
        setDeleteActionLoading(true);
        try {
            const { item, occurrenceState, segment } = recurringDeleteTarget;
            const scheduledDate = occurrenceState?.scheduledDate || getFinancialDate(item);
            const occurrenceKey = occurrenceState?.occurrenceKey || item.occurrenceKey;
            await skipRecurringOccurrence({
                accountId: currentAccountId,
                actorUid: currentUser.uid,
                segmentId: segment.id,
                scheduledDate,
                occurrenceKey,
                allowRealized,
            });
            setRecurringDeleteTarget(null);
            retryTransactions();
            showSuccess('A ocorrência foi excluída.', 'Movimentação recorrente excluída');
        } catch (error) {
            console.error('Erro ao excluir ocorrência recorrente:', error);
            showError(remoteOperationMessage(error, error?.code === 'RECURRING_DELETION_REALIZED_CONFIRMATION_REQUIRED'
                ? 'Confirme explicitamente a exclusão da movimentação já realizada.'
                : 'Não foi possível excluir esta ocorrência. Nenhuma alteração parcial foi aplicada.'));
        } finally {
            deleteActionInFlight.current = false;
            setDeleteActionLoading(false);
        }
    };

    const handleDeleteRecurringThisAndFollowing = async () => {
        if (!recurringDeleteTarget || deleteActionInFlight.current) return;
        deleteActionInFlight.current = true;
        setDeleteActionLoading(true);
        try {
            const { item, occurrenceState, segment } = recurringDeleteTarget;
            const scheduledDate = occurrenceState?.scheduledDate || getFinancialDate(item);
            const occurrenceKey = occurrenceState?.occurrenceKey || item.occurrenceKey;
            const result = await cancelRecurringSeriesFromOccurrence({
                accountId: currentAccountId,
                actorUid: currentUser.uid,
                segmentId: segment.id,
                expectedRevision: segment.revision,
                scheduledDate,
                occurrenceKey,
            });
            setRecurringDeleteTarget(null);
            retryTransactions();
            showSuccess(
                result.preservedRealizedTransactions > 0
                    ? 'A recorrência foi encerrada. Movimentações já realizadas foram preservadas.'
                    : 'A recorrência foi encerrada a partir desta ocorrência.',
                'Recorrência atualizada'
            );
        } catch (error) {
            console.error('Erro ao excluir recorrência futura:', error);
            showError(remoteOperationMessage(error, error?.code === 'RECURRING_DELETION_CONFLICT'
                ? 'Esta recorrência foi alterada em outro lugar. Reabra e tente novamente.'
                : 'Não foi possível encerrar a recorrência. Nenhuma alteração parcial foi aplicada.'));
        } finally {
            deleteActionInFlight.current = false;
            setDeleteActionLoading(false);
        }
    };
    const handleToggleStatus =
        async (item) => {
            try {
                const nextStatus =
                    await toggleTransactionStatus(
                        item
                    );

                if (
                    nextStatus ===
                    TRANSACTION_STATUS.PENDING
                ) {
                    showSuccess(
                        'A movimentação voltou para pendente.',
                        'Status atualizado'
                    );
                } else if (
                    item.tipo ===
                    TRANSACTION_TYPES.INCOME
                ) {
                    showSuccess(
                        'A receita foi marcada como recebida.',
                        'Receita recebida'
                    );
                } else {
                    showSuccess(
                        'A despesa foi marcada como paga.',
                        'Despesa paga'
                    );
                }
                retryTransactions();
            } catch (error) {
                console.error(
                    'Erro ao alterar status:',
                    error
                );

                showError(
                    remoteOperationMessage(error, 'Não foi possível alterar o status.')
                );
            }
        };


    const contextPeriodKey = JSON.stringify([currentAccountId, temporal.mode, activeOccurrenceRange]);
    const resolvedContextPeriod = hasResolvedDataset ? contextPeriodKey : '';
    const updatingPeriodLabel = temporal.title || formatYearMonthLabel(selectedPeriod, { includeYear: false })?.toLocaleLowerCase('pt-BR');

    return (
        <div className="movements-page movements-page--app">
            <AccountPageHeader
                title="Movimentações"
                wide
                onBack={() => navigate(-1)}
            />

            <div className="movements-page__container">
                <PageIntro
                    className="movements-page__description"
                    description="Acompanhe entradas, saídas e a distribuição do seu dinheiro."
                />
                {temporal.mode !== 'month' ? (
                    <section className={`movements-range-context${isAllView ? ' movements-range-context--all' : ''}`} aria-label="Período ativo">
                        <strong>{temporal.title}</strong>
                        {activeOccurrenceRange && <span>{formatMovementRangeLabel(activeOccurrenceRange, temporal.mode === 'range')}</span>}
                        {isAllView && <span>Histórico persistido, sem expandir ocorrências previstas.</span>}
                        {isRecentView && <span>Histórico até hoje, sem compromissos futuros.</span>}
                    </section>
                ) : (
                    <PeriodNavigator
                        value={selectedPeriod}
                        currentPeriod={currentPeriod}
                        onChange={(period) => {
                            setPeriodDirection(compareYearMonthKeys(period, selectedPeriod) < 0 ? 'backward' : 'forward');
                            const periodParam = toYearMonthParam(period);
                            if (periodParam) {
                                setSearchParams((currentParams) => {
                                    const nextParams = new URLSearchParams(currentParams);
                                    nextParams.set('period', periodParam);
                                    nextParams.delete('edit');
                                    nextParams.delete('view');
                                    nextParams.delete('start');
                                    nextParams.delete('end');
                                    return nextParams;
                                }, { replace: true });
                            }
                        }}
                    />
                )}

                {transactionsError && !loading ? (
                    <section className="nf-data-error" role="alert">
                        <strong>Não foi possível carregar suas movimentações.</strong>
                        <span>{temporal.error ? 'Escolha um período personalizado com datas válidas nos filtros.' : 'Verifique a conexão com o Firestore e tente novamente.'}</span>
                        <button type="button" onClick={temporal.error ? () => setShowFilterModal(true) : retryTransactions}>{temporal.error ? 'Corrigir período' : 'Tentar novamente'}</button>
                    </section>
                ) : (
                <PeriodContentTransition
                    loading={loading}
                    period={contextPeriodKey}
                    resolvedPeriod={resolvedContextPeriod}
                    direction={periodDirection}
                    label={updatingPeriodLabel ? `Atualizando ${updatingPeriodLabel}...` : 'Atualizando período...'}
                >
                <main className="movements-page__content">
                    <section className="movements-hero-card">
                        <div className="movements-hero-card__top">
                            <div>
                                <span>{temporal.flowTitle}</span>
                                <SensitiveMoney as="strong" variant="hero" className="nf-financial-value" valueCents={balance} />
                                <small>{visibleChartSeries.length === 2
                                    ? 'Saldo entre receitas e despesas'
                                    : visibleChartSeries[0] === 'income' ? 'Somente receitas no período'
                                    : visibleChartSeries[0] === 'expense' ? 'Somente despesas no período'
                                    : 'Nenhuma movimentação pendente no período'}</small>
                            </div>
                            <PiChartLineUp className="movements-hero-card__mark" aria-hidden="true" />
                        </div>

                        <FinancialFlowChart data={chartData} visibleSeries={visibleChartSeries} accountId={currentAccountId} periodKey={contextPeriodKey} />

                        {visibleChartSeries.length > 0 && (
                            <div className={`movements-hero-card__metrics${visibleChartSeries.length === 1 ? ' movements-hero-card__metrics--single' : ''}`} aria-label="Totais do período">
                                {visibleChartSeries.includes('income') && <div className="is-income">
                                    <PiTrendUp aria-hidden="true" />
                                    <span>Receitas</span>
                                    <SensitiveMoney as="strong" className="nf-financial-value" valueCents={incomeTotal} semantic="income" />
                                </div>}
                                {visibleChartSeries.includes('expense') && <div className="is-expense">
                                    <PiTrendDown aria-hidden="true" />
                                    <span>Despesas</span>
                                    <SensitiveMoney as="strong" className="nf-financial-value" valueCents={expenseTotal} semantic="expense" />
                                </div>}
                            </div>
                        )}
                    </section>

                    <section className="movements-category-card" aria-labelledby="movements-category-title">
                        <div className="movements-section-heading">
                            <div>
                                <span>Análise</span>
                                <h2 id="movements-category-title">{categoryContext.title}</h2>
                            </div>
                            {categories.length > 0 && (
                                <div className="movements-section-heading__total">
                                    <span>Total</span>
                                    <SensitiveMoney as="strong" className="nf-financial-value" valueCents={categoryTotal} semantic={categoryContext.semantic} />
                                </div>
                            )}
                        </div>

                        <div className="movements-category-card__content">
                            {categories.length > 0 && (
                                <div className="movements-donut" style={{ background: categoryGradient }}>
                                    <div className="movements-donut__center">
                                        <strong>{categories.length}</strong>
                                        <span>{categories.length === 1 ? 'categoria' : 'categorias'}</span>
                                    </div>
                                </div>
                            )}

                            <div className="movements-category-list">
                                {categories.length > 0 ? categories.map((category, index) => (
                                    <div
                                        className="movements-category-list__item"
                                        key={category.name}
                                        style={{ '--category-color': CATEGORY_COLORS[index % CATEGORY_COLORS.length] }}
                                    >
                                        <div className="movements-category-list__heading">
                                            <span>{category.name}</span>
                                            <SensitiveMoney as="strong" className="nf-financial-value" valueCents={category.value} semantic={categoryContext.semantic} />
                                        </div>
                                        <div className="movements-category-list__meta">
                                            <span>{category.percentage.toFixed(0)}%</span>
                                            <div className="movements-category-list__track" aria-hidden="true">
                                                <i style={{ inlineSize: `${Math.min(category.percentage, 100)}%` }} />
                                            </div>
                                        </div>
                                    </div>
                                )) : (
                                    <p className="movements-category-list__empty">{categoryContext.empty}</p>
                                )}
                            </div>
                        </div>

                        {temporal.mode === 'month' && (
                            <Link className="movements-category-card__link" to={'/planejamento?period=' + toYearMonthParam(selectedPeriod)}>
                                Ver planejamento <span aria-hidden="true">→</span>
                            </Link>
                        )}
                    </section>
                    <section className="movements-history">
                        {currentAccount?.type !== 'personal' && <MemberAttributionFilter members={members} transactions={resolvedItems} value={memberFilter} onChange={setMemberFilter} />}
                        <TransactionsSection
                            key={temporal.mode}
                            receitas={visibleReceitas}
                            despesas={visibleDespesas}
                            searchQuery={searchQuery}
                            onSearchChange={setSearchQuery}
                            quickFilter={quickFilter}
                            onQuickFilterChange={setQuickFilter}
                            onOpenFilters={() =>
                                setShowFilterModal(true)
                            }
                            filtersActive={
                                filtersActive
                            }
                            onClearFilters={
                                resetFilters
                            }
                            onNew={() =>
                                openAddModal()
                            }
                            contextDescription={temporal.mode === 'month' ? undefined : 'Receitas e despesas deste contexto financeiro.'}
                            defaultSortDirection={isRecentView ? 'desc' : undefined}
                            onOpenVirtual={handleRequestEdit}
                            onEdit={handleRequestEdit}
                            onDelete={handleRequestDelete}
                            onShare={currentAccount?.type === 'personal' && accounts.some((account) => account.type === 'family' && ['owner', 'admin', 'editor'].includes(account.role)) ? setShareItem : undefined}
                            onChangeAttribution={setAttributionItem}
                            onToggleStatus={
                                handleToggleStatus
                            }
                            hasPeriodTransactions={
                                transactionsCount > 0 ||
                                periodReceitas.length +
                                periodDespesas.length >
                                0
                            }
                        />
                    </section>
                </main>
                </PeriodContentTransition>
                )}
            </div>

            {showFilterModal && (
                <FiltersModal
                    accountId={currentAccountId}
                    onClose={() =>
                        setShowFilterModal(false)
                    }
                    filterStartDate={
                        filterStartDate
                    }
                    filterEndDate={
                        filterEndDate
                    }
                    filterStatus={
                        filterStatus
                    }
                    filterCategory={
                        filterCategory
                    }
                    onApplyPeriod={applyTemporalPeriod} periodScope={temporal.mode}
                    setFilterStatus={
                        setFilterStatus
                    }
                    setFilterCategory={
                        setFilterCategory
                    }
                    filtersActive={
                        filtersActive
                    }
                    resetFilters={
                        resetFilters
                    }
                    allPeriods={
                        allPeriods
                    }
                    members={members}
                    transactions={resolvedItems}
                    memberFilter={memberFilter}
                    setMemberFilter={setMemberFilter}
                />
            )}

            {showAddModal && (
                <AddItemModal
                    accountId={currentAccountId}
                    currentRole={currentRole}
                    members={members}
                    defaultAttributedToUid={currentUser?.uid || ''}
                    defaultAttributedToName={currentUser?.displayName || 'Membro'}
                    show
                    initialType={
                        addModalInitialType
                    }
                    selectedPeriod={
                        selectedPeriod
                    }
                    onClose={() =>
                        setShowAddModal(false)
                    }
                    onSave={handleAdd}
                    onSaveRecurring={handleAddRecurring}
                />
            )}

            {(editLookupLoading || deleteLookupLoading) && (
                <div className="movements-edit-lookup" role="status">Verificando recorrência…</div>
            )}

            {recurringEditTarget && (
                <RecurringEditScopeSheet
                    loading={scopeActionLoading}
                    onClose={() => setRecurringEditTarget(null)}
                    onEditOnlyThis={handleEditOnlyThis}
                    onEditThisAndFollowing={handleEditThisAndFollowing}
                />
            )}

            {recurringDeleteTarget && (
                <RecurringDeleteScopeSheet
                    item={recurringDeleteTarget.item}
                    loading={deleteActionLoading}
                    onClose={() => setRecurringDeleteTarget(null)}
                    onDeleteOnlyThis={handleDeleteRecurringOnlyThis}
                    onDeleteThisAndFollowing={handleDeleteRecurringThisAndFollowing}
                />
            )}
            {editItem && (
                <EditItemModal
                    accountId={currentAccountId}
                    currentRole={currentRole}
                    members={members}
                    item={editItem}
                    recurrenceContext={editOccurrenceState}
                    show
                    onClose={() => {
                        setEditItem(null);
                        setEditOccurrenceState(null);
                    }}
                    onSave={handleEdit}
                />
            )}

            {shareItem && (
                <ShareTransactionSheet
                    transaction={shareItem}
                    sourceAccountId={currentAccountId}
                    accounts={accounts}
                    onClose={() => setShareItem(null)}
                />
            )}

            {attributionItem && (
                <ChangeAttributionSheet
                    item={attributionItem}
                    members={members}
                    onSave={handleChangeAttribution}
                    onClose={() => setAttributionItem(null)}
                />
            )}

            {deleteItem && (
                <ConfirmDeleteModal
                    item={deleteItem}
                    onClose={() =>
                        setDeleteItem(null)
                    }
                    onDelete={handleDelete}
                />
            )}

            <button
                type="button"
                className="movements-page__fab"
                onClick={() =>
                    openAddModal()
                }
                aria-label="Nova movimentação"
            >
                <PiPlus aria-hidden="true" />
            </button>
        </div>
    );
};

export default Movements;






